import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import Grid from '@mui/material/Grid2';
import Divider from '@mui/material/Divider';
import SmartUserTableToolbar from '~/FielderElements/SmartUserTable/SmartUserTableToolbar';
import SmartUserAuxToolbar from '~/FielderElements/SmartUserTable/SmartUserAuxToolbar';
import HeaderCheckboxes from '../../../oldComponents/SmartTable/HeaderCheckboxes';
import LogoBg from '@assets/icons/fielder-logo-404.svg';
import {
	createSmartFielderTheme,
	SmartAlertBannerContainer, SmartAvatarTitle, SmartIconButton, SmartTableContainerSx, SmartTitleTypography
} from '~/FielderElements/SmartTaskTable/SmartStyles';
import { getSmartUserTableColumns, getUserLanguage } from '~/FielderElements/SmartTaskTable/SmartTableUtils';
import { Box, Tooltip } from '@mui/material';
import { ClearAll as ClearAllIcon } from '@mui/icons-material';
import SmartUserActionPopup from '~/FielderElements/Utils/SmartFielderActionPopup';
import { FIELDER } from '@core/constants/base.constants';
import { useLanguage } from 'Context/LanguageContext';
import SmartRouteAuxToolbar from '~/FielderElements/SmartUserTable/SmartRouteAuxToolbar';
import { ThemeProvider } from '@mui/material/styles';

const SmartUserTable = React.forwardRef((
	{ elements = [], titleComponent = 'Fielder', showCheckboxComponent, cookieTable, cookieName, customActions,
		deleteAction, onRowClick, onRowMenuClick, paginationHidden, totalCount, onPaginationChange, height,
		enableDefaultActions = true, userToolbarProps = {}, toolbarFilterProps, auxToolbarProps, auxToolbarRoute,
		onCheckBoxSelected, smartTableId, ...rest
	}, ref) => {
	const lan = useLanguage();
	const userLanguage = getUserLanguage(lan, smartTableId,
		auxToolbarProps && Object.keys(auxToolbarProps).length > 0 ? auxToolbarProps.optionSelected : null);
	const getColumns = useCallback(() =>
		getSmartUserTableColumns(elements, smartTableId, lan, { onRowMenuClick }),
		[elements, lan, smartTableId, onRowMenuClick]);
	const [columns, setColumns] = useState(getColumns);
	const rows = useMemo(() => [...elements], [elements]);
	const [rowSelection, setRowSelection] = useState({});
	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
	const [tableDensity, setTableDensity] = useState('compact');
	const selectedRowsRef = useRef([]);
	const selectedIndexesRef = useRef([]);
	const prevElementsRef = useRef(elements);
	//Dark mode and theme
	const [mode, setMode] = useState('light');
	const toggleTheme = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
	const smartTheme = useMemo(() => createSmartFielderTheme(mode), [mode]);
	const clearSelectionHandler = () => {
		selectedRowsRef.current = [];
		selectedIndexesRef.current = [];
		setRowSelection({});
	}
	useImperativeHandle(ref, () => ({
		getState: () => ({
			selectedRows: selectedRowsRef.current,
			selectedIndexes: selectedIndexesRef.current
		}),
		resetSelection: () => {
			clearSelectionHandler();
		},
	}), []);
	useEffect(() => {
		if (prevElementsRef.current !== elements) {
			const prevKeys = Object.keys(prevElementsRef.current?.[0] || {}).sort();
			const currentKeys = Object.keys(elements?.[0] || {}).sort();
			const columnsChanged = prevKeys.join(',') !== currentKeys.join(',');
			if (columnsChanged) setColumns(getColumns());
			clearSelectionHandler();
			prevElementsRef.current = elements;
		} else setColumns(getColumns());
	}, [elements, lan, getColumns]);
	useEffect(() => {
		if (onPaginationChange) onPaginationChange(pagination);
	}, [pagination]);
	const closeActionHandler = useCallback(() => {
		clearSelectionHandler();
	}, []);
	const [columnOrder, setColumnOrder] = useState(() => {
		const order = columns.map(col => col.accessorKey);
		return order.includes('accountStatus')
			? ['mrt-row-select', 'accountStatus', ...order.filter(k => k !== 'accountStatus')]
			: ['mrt-row-select', ...order];
	});
	const [columnPinning, setColumnPinning] = useState(() => ({
		left: [],
		right: ['agentTeamList'],
	}));
	return (
		<>
			{smartTableId !== FIELDER.smartTableId.ROUTES && (<Grid container sx={{ pb: 2 }}>
				<SmartAvatarTitle src={LogoBg} />
				<SmartTitleTypography variant='h5'>{titleComponent}</SmartTitleTypography>
			</Grid>)}
			<Divider />
			<ThemeProvider theme={smartTheme}>
				<MaterialReactTable
					columns={columns}
					data={rows}
					enableColumnOrdering
					enableRowSelection
					enableColumnPinning={true}
					enableStickyHeader={true}
					enableGlobalFilter={false}
					onDensityChange={setTableDensity}
					onColumnPinningChange={setColumnPinning}
					enablePagination={!paginationHidden}
					pageSizeOptions={[10, 25, 50, 100]}
					manualPagination
					rowCount={totalCount ?? rows.length}
					onPaginationChange={(updater) => {
						const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
						setPagination(newPagination);
					}}
					muiTableHeadCellProps={{
						align: 'center'
					}}
					enableToolbarInternalActions={enableDefaultActions}
					muiTableContainerProps={{ sx: SmartTableContainerSx(height) }}
					state={{ rowSelection, pagination, density: tableDensity, columnOrder, columnPinning }}
					muiTableBodyCellProps={({ column }) => ({ 'data-column-id': column.id })}
					localization={userLanguage}
					renderTopToolbarCustomActions={({ table }) => {
						const hasToolbar = toolbarFilterProps && Object.keys(userToolbarProps).length > 0;
						const hasAuxToolbar = auxToolbarProps && Object.keys(auxToolbarProps).length > 0;
						const hasAuxToolbarRoute = auxToolbarRoute && Object.keys(auxToolbarRoute).length > 0;
						if (hasAuxToolbar) {
							return (
								<SmartUserAuxToolbar
									{...auxToolbarProps}
									showCheckboxComponent={showCheckboxComponent}
									auxToolbarActions={
									<HeaderCheckboxes
										totalColums={columns}
										updateColums={() => setColumns(getColumns())}
										action={deleteAction} customActions={customActions}
										cookieTable={cookieTable}
										cookieName={cookieName} />}
									table={table}
								/>
							);
						} else if (hasToolbar) {
							return (
								<SmartUserTableToolbar
									{...userToolbarProps}
									filterProps={toolbarFilterProps}
									showCheckboxComponent={showCheckboxComponent}
									auxToolbarActions={
									<HeaderCheckboxes
										totalColums={columns}
										updateColums={() => setColumns(getColumns())}
										action={deleteAction} customActions={customActions}
										cookieTable={cookieTable}
										cookieName={cookieName} />}
									table={table}
								/>
							);
						} else if (hasAuxToolbarRoute) {
							return (
								<SmartRouteAuxToolbar
									{...auxToolbarRoute}
									showCheckboxComponent={showCheckboxComponent}
									auxToolbarActions={
									<HeaderCheckboxes
										totalColums={columns}
										updateColums={() => setColumns(getColumns())}
										action={deleteAction} customActions={customActions}
										cookieTable={cookieTable}
										cookieName={cookieName} />}
									table={table}
								/>
							);
						}
						return null;
					}}
					muiTableBodyRowProps={({ row }) => ({
						onClick: (event) => {
							const columnElement = event.target.closest('[data-column-id]');
							const columnId = columnElement?.getAttribute('data-column-id');
							onRowClick?.(row.index, row.original, { id: columnId });
						},
						style: { cursor: 'pointer' },
					})}
					onRowSelectionChange={(updater) => {
						const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
						const selectedIndexes =
							Object.keys(newSelection).map((id) => parseInt(id, 10));
						const selectedRows = selectedIndexes.map((i) => rows[i]);
						selectedRowsRef.current = selectedRows;
						selectedIndexesRef.current = selectedIndexes;
						setRowSelection(newSelection);
						onCheckBoxSelected?.(selectedIndexes, selectedRows);
					}}
					positionToolbarAlertBanner="bottom"
					renderToolbarAlertBannerContent={() => (
						<Box sx={SmartAlertBannerContainer}>
							<Tooltip arrow title={lan.clearSelection || 'Clean selection'}>
								<SmartIconButton
									id='my-operation-task-clear-selection-btn' size='small' onClick={() => closeActionHandler()}
								>
									<ClearAllIcon />
								</SmartIconButton>
							</Tooltip>
						</Box>
					)}
					{...rest}
				/>
			</ThemeProvider>
			{selectedIndexesRef.current.length > 0  && (
				smartTableId !== FIELDER.smartTableId.GLOBAL_USER && smartTableId !== FIELDER.smartTableId.ROUTES &&
				smartTableId !== FIELDER.smartTableId.AGENT) && (
				<SmartUserActionPopup
					selectedRows={selectedRowsRef.current}
					selectedCount={selectedIndexesRef.current.length}
					closeAction={() => closeActionHandler()}
				/>
			)}
		</>
	);
});

export default SmartUserTable;
